/* DESIGN.md §9.1 `Toast`/`ToastAction`. Stacked, bottom-right, `--elev-2` (menus/popovers/toasts
   share that step per §4.4), `--z-toast` (the one place that token may appear, per §4.5). */
.toast-region {
  position: fixed;
  /* MVP-703: clear of the notch and the home indicator once the page runs edge to edge. */
  right: max(var(--space-4), env(safe-area-inset-right));
  bottom: max(var(--space-4), env(safe-area-inset-bottom));
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  /* MVP-409: was 360px, and the column this floats above is `--panel-w-sm` (320px) — the cockpit's
     side rail. The 40px of overhang landed on live controls, so reaching for one of them put the
     pointer on the toast, which is exactly the gesture that used to pin it open forever (see
     `MAX_HOLD_MS` in Toast.tsx for the other half of that trap). Matching the rail means the toast
     covers controls it is stacked on top of, which is unavoidable, and none that it merely passes. */
  width: min(var(--panel-w-sm), calc(100vw - var(--space-8)));
  pointer-events: none;
}

/* MVP-261: keyframe rules restart from frame zero every time they are re-triggered; a `transition`
   retargets from wherever the element currently sits. On a surface that adds/removes in bursts —
   a KJ queuing three songs in a row fires three toasts inside a second — that difference is the
   whole bug: the old enter keyframe rule made each new toast snap in from `opacity: 0` even while
   a sibling toast was still mid-animation, instead of the stack settling smoothly.
   `@starting-style` supplies the pre-transition "from" state on first paint, which is what that
   keyframe rule's `from {}` used to do. */
.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--bg-raised);
  border: 1px solid var(--border-default);
  box-shadow: var(--elev-2);
  pointer-events: auto;
  opacity: 1;
  transform: translateY(0);
  transition: opacity var(--dur-3) var(--ease-out), transform var(--dur-3) var(--ease-out);

  @starting-style {
    opacity: 0;
    transform: translateY(var(--space-2));
  }
}

/* MVP-261: exit is deliberately the FASTER of the two (`--dur-2` = 150ms vs `--dur-3` = 220ms in)
   — slow where the KJ is reading, fast where the system is clearing. Set by Toast.tsx once the
   toast's timer (or its own dismiss/action click) fires; the component keeps the toast mounted
   until this transition ends so it can be removed cleanly instead of vanishing at max-height. */
.toast[data-leaving] {
  opacity: 0;
  transform: translateY(var(--space-2));
  transition-duration: var(--dur-2);
}

.toast--success .toast__icon {
  color: var(--ok-500);
}
.toast--info .toast__icon {
  color: var(--info-500);
}
.toast--error .toast__icon {
  color: var(--danger-500);
}

.toast__icon {
  flex: none;
  margin-top: 2px;
}
.toast__body {
  flex: 1;
  min-width: 0;
}
.toast__title {
  color: var(--fg-primary);
}
.toast__description {
  color: var(--fg-secondary);
}
.toast__action {
  flex: none;
}

/* MVP-261: no local reduced-motion override needed. base.css:139's global rule clamps
   `transition-duration` to 1ms `!important` on every element, which already reaches the
   `transition:` declarations above — unlike the old none-value overrides this file used to carry
   on the enter/leave keyframe rules, which stopped doing anything useful the moment those rules
   were replaced by transitions (an override for a mechanism that no longer exists silently no-ops).
   Kept out of this file so there is one clamp to keep correct, not two that can drift apart. */

/* MVP-462: the region `AlertToast` portals into. Top-centre rather than the toast stack's
   bottom-right corner, because these are the page's own failures — they belong where the page's
   header is, and sharing a corner with the cut receipt would mean a queue toast and a "save failed"
   alert fighting over the same 320px.

   No fixed width: a Thai failure sentence set to `--panel-w-sm` wraps to four lines, and these
   appear one at a time. The content sizes itself and the viewport is the only cap. */
.alert-toast-region {
  position: fixed;
  top: max(var(--space-4), env(safe-area-inset-top)); /* MVP-702: clear of a translucent status bar */
  /* MVP-691: inset both sides, not `left: 50%` + translate. A fixed box placed at left:50% is
     shrink-to-fit against the HALF of the viewport to its right, so on a 375px phone the alert got
     ~170px and a Thai sentence broke into five lines over the Sheet's title. Spanning the viewport
     minus the gutters and centring the children gives the full width to wrap in. */
  left: var(--space-4);
  right: var(--space-4);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  max-width: calc(100vw - var(--space-8));
  /* The region spans the width it is given; only the alerts inside it may be clicked, so the page
     underneath stays usable either side of them. */
  pointer-events: none;
}

/* MVP-691: on a phone the top of the screen is where a Sheet keeps its title and its close
   button, and a top alert sat exactly on both. At or below the 768px breakpoint the alerts
   stack at the bottom instead, clear of the home indicator. */
@media (max-width: 768px) {
  .alert-toast-region {
    top: auto;
    bottom: calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
  }
}

.alert-toast-region .inline-alert {
  max-width: 100%;
  pointer-events: auto;
  border: 1px solid var(--border-default);
  box-shadow: var(--elev-2);
}
